import { useQuery } from '@tanstack/react-query';
import { ADMIN_PUBKEY, SPONSORED_LIST_D_TAG } from '@/lib/admin';
import { discoveryPool, discoverySignal } from '@/lib/appRelays';

/**
 * Fetch the list of sponsored merchant pubkeys.
 *
 * The admin maintains a kind 30078 addressable event with:
 * - d = "bch-sponsored-merchants"
 * - p tags listing each sponsored merchant's pubkey
 *
 * Only the admin's pubkey is trusted — filtered at the relay level.
 *
 * Returns a plain array (not a Set) so the result survives the persisted
 * localStorage query cache round-trip.
 */
export function useSponsoredMerchants() {
  return useQuery({
    queryKey: ['nostr', 'sponsored-merchants'],
    queryFn: async (c) => {
      const events = await discoveryPool.query(
        [{
          kinds: [30078],
          authors: [ADMIN_PUBKEY],
          '#d': [SPONSORED_LIST_D_TAG],
          limit: 1,
        }],
        { signal: discoverySignal(c.signal) },
      );

      if (events.length === 0) return [] as string[];

      const event = events[0];
      const sponsoredPubkeys: string[] = [];

      for (const tag of event.tags) {
        if (tag[0] === 'p' && tag[1]) {
          sponsoredPubkeys.push(tag[1]);
        }
      }

      return sponsoredPubkeys;
    },
    staleTime: 10 * 60 * 1000,
  });
}
